import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Link2, Plus, Trash2, Copy, Search, Eye,
  Calendar, User, FileText
} from "lucide-react";
import { toast } from "sonner";
import Sidebar from "@/components/Sidebar";
import { copyToClipboard } from "@/lib/clipboard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const BRAND_DOMAIN = "miwap.link";

export default function LinksPage({ user }) {
  const navigate = useNavigate();
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newLink, setNewLink] = useState({
    name: "",
    slug: "",
    description: "",
    distribution_type: "round_robin"
  });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchLinks();
  }, []);

  const fetchLinks = async () => {
    try {
      const response = await fetch(`${API}/links`, { credentials: "include" });
      if (response.ok) {
        setLinks(await response.json());
      }
    } catch (error) {
      console.error("Error fetching links:", error);
      toast.error("Error al cargar enlaces");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateLink = async (e) => {
    e.preventDefault();
    
    const trimmedName = newLink.name.trim();
    const trimmedSlug = newLink.slug.trim();
    const trimmedDescription = newLink.description.trim();
    
    if (!trimmedName) {
      toast.error("El nombre del enlace es requerido");
      return;
    }
    if (!trimmedSlug) {
      toast.error("El slug (URL corta) es requerido");
      return;
    }
    if (!/^[a-z0-9-]+$/.test(trimmedSlug)) {
      toast.error("El slug solo puede contener letras minúsculas, números y guiones");
      return;
    }
    if (trimmedSlug.length < 3) {
      toast.error("El slug debe tener al menos 3 caracteres");
      return;
    }
    if (!trimmedDescription) {
      toast.error("La descripción (causa de creación) es obligatoria");
      return;
    }

    setCreating(true);
    try {
      const response = await fetch(`${API}/links`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          name: trimmedName,
          slug: trimmedSlug,
          description: trimmedDescription,
          distribution_type: newLink.distribution_type
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        toast.success("Enlace creado exitosamente");
        // Force navigation using form trick
        const form = document.createElement('form');
        form.method = 'GET';
        form.action = `/enlaces/${data.link_id}`;
        document.body.appendChild(form);
        form.submit();
        return;
      } else {
        toast.error(data.detail || data.message || "Error al crear enlace");
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("Error de conexión al crear enlace");
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteLink = async (linkId) => {
    console.log("Delete clicked for:", linkId);
    try {
      const response = await fetch(`${API}/links/${linkId}`, {
        method: "DELETE",
        credentials: "include"
      });
      console.log("Delete response:", response.status);
      if (response.ok) {
        setLinks(prev => prev.filter(l => l.link_id !== linkId));
        toast.success("Enlace eliminado");
      } else {
        const data = await response.json();
        console.log("Delete error data:", data);
        toast.error(data.detail || "Error al eliminar");
      }
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Error al eliminar");
    }
  };

  const copyLink = async (slug) => {
    const success = await copyToClipboard(`${BRAND_DOMAIN}/${slug}`);
    if (success) {
      toast.success("Enlace copiado");
    } else {
      toast.error("No se pudo copiar");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredLinks = links.filter(link => 
    link.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    link.slug.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (link.description && link.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="min-h-screen bg-background" data-testid="links-page">
      <Sidebar user={user} />

      <main className="md:ml-64 pt-14 md:pt-0 p-4 md:p-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl md:text-3xl font-heading font-black">Enlaces</h1>
              <p className="text-muted-foreground text-sm">Gestiona tus enlaces de redirección</p>
            </div>
            <Button 
              className="bg-primary hover:bg-black rounded-none w-full sm:w-auto"
              onClick={() => setShowCreateDialog(true)}
              data-testid="new-link-button"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nuevo Enlace
            </Button>
          </div>

          {/* Search */}
          <div className="mb-4 md:mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por nombre, slug o descripción..."
                className="pl-10 rounded-none"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {/* Links List */}
          {loading ? (
            <div className="space-y-3">
              {[...Array(3)].map((_, i) => <div key={i} className="h-28 skeleton"></div>)}
            </div>
          ) : filteredLinks.length === 0 ? (
            <Card className="border-border rounded-none">
              <CardContent className="py-12 text-center">
                <Link2 className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                <h3 className="font-heading text-lg font-bold mb-2">
                  {searchTerm ? "Sin resultados" : "No tienes enlaces"}
                </h3>
                <p className="text-muted-foreground text-sm mb-4">
                  {searchTerm ? "Intenta otro término" : "Crea tu primer enlace"}
                </p>
                {!searchTerm && (
                  <Button className="rounded-none" onClick={() => setShowCreateDialog(true)}>
                    <Plus className="w-4 h-4 mr-2" />Crear Enlace
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {filteredLinks.map((link) => (
                <Card key={link.link_id} className="border-border rounded-none hover:border-primary/50 transition-colors">
                  <CardContent className="p-4">
                    <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                      {/* Info principal */}
                      <div className="flex-1 min-w-0 cursor-pointer" onClick={() => navigate(`/enlaces/${link.link_id}`)}>
                        <div className="flex items-center gap-2 mb-2">
                          <div className={`w-2 h-2 rounded-full flex-shrink-0 ${link.is_active ? "bg-green-500" : "bg-red-500"}`}></div>
                          <h3 className="font-heading font-bold text-base md:text-lg truncate">{link.name}</h3>
                        </div>
                        
                        <code className="text-sm text-primary bg-primary/10 px-2 py-0.5 rounded">
                          {BRAND_DOMAIN}/{link.slug}
                        </code>
                        
                        {/* Descripción/Causa */}
                        {link.description && (
                          <div className="mt-3 flex items-start gap-2 text-sm text-muted-foreground">
                            <FileText className="w-4 h-4 flex-shrink-0 mt-0.5" />
                            <p className="line-clamp-2">{link.description}</p>
                          </div>
                        )}
                        
                        {/* Metadata */}
                        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {formatDate(link.created_at)}
                          </span>
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {link.user_name || user?.name || "Usuario"}
                          </span>
                          <span>{link.destinations?.length || 0} destinos</span>
                        </div>
                      </div>

                      {/* Stats y acciones */}
                      <div className="flex items-center gap-3 md:gap-4 flex-shrink-0">
                        <div className="text-right">
                          <p className="font-heading font-bold text-2xl">{link.total_clicks || 0}</p>
                          <p className="text-xs text-muted-foreground">clics</p>
                        </div>

                        <div className="flex items-center gap-1">
                          <Button variant="outline" size="icon" className="rounded-none h-9 w-9" onClick={() => copyLink(link.slug)}>
                            <Copy className="w-4 h-4" />
                          </Button>
                          
                          <Button variant="outline" size="icon" className="rounded-none h-9 w-9" onClick={() => navigate(`/enlaces/${link.link_id}`)}>
                            <Eye className="w-4 h-4" />
                          </Button>

                          <Button 
                            variant="outline" 
                            size="icon" 
                            className="rounded-none h-9 w-9 text-destructive hover:bg-destructive hover:text-white"
                            onClick={() => handleDeleteLink(link.link_id)}
                            data-testid={`delete-link-${link.link_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="rounded-none w-[95vw] max-w-md mx-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Crear Nuevo Enlace</DialogTitle>
            <DialogDescription>Configura un nuevo enlace de redirección</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateLink}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Nombre del Enlace <span className="text-destructive">*</span></Label>
                <Input
                  placeholder="Ej: Equipo de Ventas Norte"
                  className="rounded-none"
                  value={newLink.name}
                  onChange={(e) => setNewLink({ ...newLink, name: e.target.value })}
                  data-testid="link-name-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Slug (URL corta) <span className="text-destructive">*</span></Label>
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground text-sm bg-muted px-2 py-1.5 border">{BRAND_DOMAIN}/</span>
                  <Input
                    placeholder="equipo-ventas-norte"
                    className="rounded-none flex-1"
                    value={newLink.slug}
                    onChange={(e) => setNewLink({ 
                      ...newLink, 
                      slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") 
                    })}
                    data-testid="link-slug-input"
                  />
                </div>
                <p className="text-xs text-muted-foreground">Solo letras minúsculas, números y guiones. Mín. 3 caracteres.</p>
              </div>
              
              <div className="space-y-2">
                <Label>Descripción / Causa de Creación <span className="text-destructive">*</span></Label>
                <Textarea
                  placeholder="Ej: Enlace para la campaña de Facebook Ads de enero 2025, distribuir leads entre el equipo de ventas de zona norte."
                  className="rounded-none min-h-[100px]"
                  value={newLink.description}
                  onChange={(e) => setNewLink({ ...newLink, description: e.target.value })}
                  data-testid="link-description-input"
                />
                <p className="text-xs text-muted-foreground">Describe el propósito de este enlace. Campo obligatorio.</p>
              </div>
              
              <div className="space-y-2">
                <Label>Tipo de Distribución</Label>
                <Select 
                  value={newLink.distribution_type}
                  onValueChange={(value) => setNewLink({ ...newLink, distribution_type: value })}
                >
                  <SelectTrigger className="rounded-none" data-testid="distribution-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-none">
                    <SelectItem value="round_robin">Round Robin (Rotación equitativa)</SelectItem>
                    <SelectItem value="weighted">Distribución Ponderada (por peso)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button type="button" variant="outline" className="rounded-none w-full sm:w-auto" onClick={() => setShowCreateDialog(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="rounded-none bg-primary hover:bg-black w-full sm:w-auto" disabled={creating} data-testid="create-link-submit">
                {creating ? "Creando..." : "Crear Enlace"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
